// src/components/homepage/CountryMap.tsx

import React, { useRef, useEffect } from 'react'; // Import React and necessary hooks
import { Swiper, SwiperSlide } from 'swiper/react'; // Import Swiper components
import { Navigation } from 'swiper'; // Import only the Navigation module from Swiper
import 'swiper/css'; // Import Swiper's core CSS
import 'swiper/css/navigation'; // Import Swiper's navigation CSS

// Import images representing different countries' maps
import AustraliaMap from '../../images/Maps/Australia.png';
import BelizeMap from '../../images/Maps/Belize map.png';
import CostaRicaMap from '../../images/Maps/CostaRica map.png';
import CubaMap from '../../images/Maps/Cuba map.png';
import NamibiaMap from '../../images/Maps/Namibia map.png';
import NepalMap from '../../images/Maps/Nepal map.png';
import SouthAmericaMap from '../../images/Maps/SouthAmerica map.png';
import ThailandMap from '../../images/Maps/Thailand map.png';
import VenezuelaMap from '../../images/Maps/Venezuela map.png';

import { SlArrowLeft, SlArrowRight } from 'react-icons/sl'; // Import arrow icons from react-icons

/**
 * CountryMap Component
 *
 * This component renders a carousel of country maps using Swiper.
 * It includes custom navigation buttons for Previous and Next slides.
 */
const CountryMap: React.FC = () => {
  /**
   * Array of image objects containing the source path for each country map.
   * This data is used to dynamically generate Swiper slides.
   */
  const images = [
    { src: AustraliaMap },
    { src: BelizeMap },
    { src: CostaRicaMap },
    { src: CubaMap },
    { src: NamibiaMap },
    { src: NepalMap },
    { src: SouthAmericaMap },
    { src: ThailandMap },
    { src: VenezuelaMap },
  ];

  // Create refs for custom navigation buttons
  const prevRef = useRef<HTMLButtonElement>(null); // Reference to the Previous button
  const nextRef = useRef<HTMLButtonElement>(null); // Reference to the Next button

  // Create a ref for Swiper instance
  const swiperRef = useRef<any>(null); // Use 'any' or a more specific type if available

  /**
   * useEffect Hook
   *
   * Assigns the Swiper's navigation elements after the component has mounted.
   * This ensures that the refs are not null when assigned.
   */
  useEffect(() => {
    if (
      swiperRef.current &&
      swiperRef.current.params.navigation &&
      typeof swiperRef.current.params.navigation !== 'boolean' &&
      prevRef.current &&
      nextRef.current
    ) {
      // Type assertion: swiper.params.navigation is of type any
      (swiperRef.current.params.navigation as any).prevEl = prevRef.current;
      (swiperRef.current.params.navigation as any).nextEl = nextRef.current;

      // Initialize and update navigation
      swiperRef.current.navigation.destroy();
      swiperRef.current.navigation.init();
      swiperRef.current.navigation.update();
    }
  }, []);

  return (
    <div className="relative -mt-[15vh] text-center w-full">
      {/* Title for the CountryMap section */}
      <h2 className="font-mumford my-[3vh] mb-[1.8vh]" style={{ fontSize: '3.2rem', color: '#6bafc1' }}>
        Explore Our Destinations
      </h2>

      {/* Carousel Container */}
      <div className="relative w-full mx-auto" style={{ maxWidth: '80%', paddingLeft: '7%', paddingRight: '7%' }}>
        {/* Previous Navigation Button */}
        <button
          ref={prevRef}
          className="absolute top-1/2 -translate-y-1/2 left-0 w-12 h-12 rounded-full border border-gray-300 bg-white shadow-lg flex items-center justify-center cursor-pointer transition-all duration-200 hover:bg-gray-100 hover:scale-105 focus:outline-none"
          aria-label="Previous Slide"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>

        {/* Swiper Component */}
        <Swiper
          modules={[Navigation]}
          navigation={{
            prevEl: prevRef.current,
            nextEl: nextRef.current,
          }}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          loop={true}
          spaceBetween={20}
          slidesPerView={4}
          breakpoints={{
            1200: { slidesPerView: 4 },
            800: { slidesPerView: 3 },
            600: { slidesPerView: 2 },
            0: { slidesPerView: 1 },
          }}
          className="w-full"
        >
          {/* Dynamically generate SwiperSlide components */}
          {images.map((image, index) => (
            <SwiperSlide key={index} className="flex justify-center">
              <div className="relative w-full max-w-[450px]">
                <img 
                  src={image.src} 
                  alt={`Country map ${index + 1}`} 
                  className="w-full h-auto rounded-lg transition-transform duration-300 hover:scale-[1.03]"
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Next Navigation Button */}
        <button
          ref={nextRef}
          className="absolute top-1/2 -translate-y-1/2 right-0 w-12 h-12 rounded-full border border-gray-300 bg-white shadow-lg flex items-center justify-center cursor-pointer transition-all duration-200 hover:bg-gray-100 hover:scale-105 focus:outline-none"
          aria-label="Next Slide"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 6 15 12 9 18" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default CountryMap; // Export the CountryMap component for use in other parts of the application
